import { Injectable, inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlTree } from '@angular/router';

import { ApolloError } from '@apollo/client/errors';
import { Observable, map, of, take } from 'rxjs';

import { CommonRoutes, GlobalRegistrationRoutes, RouteData } from '@hdms/shared/common-types';
import { EmptyRouteGuard, PreloadType, RedirectService, RouterHelperService } from '@hdms/shared/util-core';

import { OauthService } from '../oauth';

import { UserContextService } from './user-context.service';

/**
 * The guard activates for registered users only.
 * If in mixed mode it lets always pass.
 * If loggedIn but not registered it redirects to registration.
 * In standalone mode, if neither registered nor loggedIn
 * it finally redirects to cloud-sso.
 *
 * Additionally, current route wil be configured for redirecting to home on logout.
 */
export const canMatchLoggedIn: CanMatchFn = route => inject(LoggedInGuard).canLoad(route);

@Injectable({
  providedIn: 'root'
})
export class LoggedInGuard extends EmptyRouteGuard {
  private routeData: RouteData;

  private readonly oauth = inject(OauthService);
  private readonly redirect = inject(RedirectService);
  private readonly userContext = inject(UserContextService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly router = inject(Router);

  override canLoad(route: Route): Observable<boolean | UrlTree> {
    this.routeData = route.data ||= {};
    this.routeData.ssoLogoutRedirectRoute = '/';

    this.userContext.requiresLogin = this.shouldRedirect;

    super.canLoad(route);

    return this.canLoad$();
  }

  private canLoad$(): Observable<boolean | UrlTree> {
    return this.env.isMixedMode || this.env.ssrCachingEnabled
      ? of(true)
      : this.userContext.update$().pipe(
          take(1),
          map(_ => this.checkContextError() || this.userContext.isRegistered || this.checkRedirect())
        );
  }

  /**
   * Returns UrlTree to
   * - empty route when redirecting to cloud-login
   * - account registration when user is pending registration
   * - not found in any other case
   * @private
   */
  private checkRedirect(): UrlTree {
    return this.checkLoginRedirect()
      ? this.createRerouteUrlTree(CommonRoutes.EMPTY)
      : this.checkRegistrationRedirect() || this.redirectToNotFound();
  }

  /**
   * Invokes a redirect to cloud-sso for logging in,
   * if standalone and user is anonymous.
   * Returns if redirecting.
   * @private
   */
  private checkLoginRedirect(): boolean {
    const shouldRedirect = this.shouldRedirect;
    if (shouldRedirect) {
      this.preloader.startLoading(PreloadType.MANUAL);
      this.oauth.getLoginUrl$().subscribe(url => this.redirect.redirectTo(url, true));
    }
    return shouldRedirect;
  }

  /**
   * Redirect to registration if logged-in
   * @private
   */
  private checkRegistrationRedirect(): UrlTree | false {
    return (
      this.userContext.isLoggedIn &&
      this.router.createUrlTree([GlobalRegistrationRoutes.BASE, GlobalRegistrationRoutes.ACCOUNT])
    );
  }

  /**
   * Redirect to 404 and set UserContext error state
   * @private
   */
  private redirectToNotFound(): UrlTree {
    this.userContext.setData({ error: new ApolloError({}) });
    return this.createRerouteUrlTree(CommonRoutes.NOT_FOUND);
  }

  /**
   * In case an error has occurred while retrieving user-data,
   * display an error page without changing the location url.
   * @private
   */
  private checkContextError(): boolean | UrlTree {
    return this.userContext.error ? this.createRerouteUrlTree(CommonRoutes.GENERIC_ERROR) : false;
  }

  private createRerouteUrlTree(route: string): UrlTree {
    return this.routerHelper.createSilentRerouteUrlTree([route]);
  }

  private get shouldRedirect(): boolean {
    return (
      this.env.isStandalone &&
      this.userContext.isAnonymous &&
      !this.routeData.preventLoginRedirect &&
      this.redirect.canRedirect()
    );
  }
}
